import React, { useEffect, useRef } from 'react';
import {
  AiOutlineDatabase, AiOutlineCloudSync, AiOutlineDashboard
} from 'react-icons/ai';
import {
  FaRobot, FaChartLine, FaBolt, FaBrain, FaRocket, FaShieldAlt
} from 'react-icons/fa';
import { FiZap, FiUsers, FiTrendingUp } from 'react-icons/fi';
import { LuBrain, LuTarget, LuSparkles, LuCpu } from 'react-icons/lu';

const features = [
  {
    icon: <AiOutlineDatabase className="text-3xl" />,
    title: "Scalable Data Architecture",
    desc: "Design and manage high-performance databases and data systems built for speed, scale, and reliability across growing applications.",
    gradient: "from-purple-500 to-pink-500",
    color: "purple"
  },
  {
    icon: <FaRobot className="text-3xl" />,
    title: "AI-Powered Development",
    desc: "Leverage intelligent tools and automation to accelerate development cycles and improve code quality across your product lifecycle.",
    gradient: "from-blue-500 to-cyan-500",
    color: "blue"
  },
  {
    icon: <FiZap className="text-3xl" />,
    title: "Real-time System Monitoring",
    desc: "Get instant insights into performance, errors, and system health to ensure your applications run smoothly at all times.",
    gradient: "from-yellow-500 to-orange-500",
    color: "yellow"
  },
  {
    icon: <LuBrain className="text-3xl" />,
    title: "Smart Performance Optimization",
    desc: "Identify bottlenecks and optimize applications using data-driven insights and engineering best practices.",
    gradient: "from-pink-500 to-rose-500",
    color: "pink"
  },
  {
    icon: <AiOutlineCloudSync className="text-3xl" />,
    title: "Seamless Integrations",
    desc: "Connect your software with third-party APIs, cloud services, and enterprise tools for a unified ecosystem.",
    gradient: "from-emerald-500 to-teal-500",
    color: "emerald"
  },
  {
    icon: <FaChartLine className="text-3xl" />,
    title: "Engineering Analytics",
    desc: "Track system performance, usage patterns, and product metrics with clear and actionable dashboards.",
    gradient: "from-indigo-500 to-purple-500",
    color: "indigo"
  },
];

const Features = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, { threshold: 0.1 });

    const elements = sectionRef.current?.querySelectorAll('.fade-up, .scale-in');
    elements?.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" ref={sectionRef} className="py-20 max-w-7xl mx-auto px-5 md:px-8">
      <div className="text-center fade-up">
        <span className="text-purple-400 font-semibold tracking-wide uppercase text-sm">
          Capabilities
        </span>
        <h2 className="text-4xl md:text-5xl font-bold mt-2">
          Everything you need to <span className="gradient-text">scale your software</span>
        </h2>
        <p className="text-gray-400 mt-3 max-w-2xl mx-auto text-lg">
          Modern engineering stack to build, deploy, and scale high-performance digital products faster.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
        {features.map((feature, idx) => (
          <div
            key={idx}
            className="glass-card rounded-2xl p-6 transition-all duration-500 hover-lift cursor-pointer group scale-in"
            style={{ transitionDelay: `${idx * 100}ms` }}
          >
            <div className={`mb-4 p-3 rounded-xl bg-gradient-to-r ${feature.gradient} bg-opacity-10 w-fit group-hover:scale-110 transition-all duration-300`}>
              <div className={`text-${feature.color}-400`}>
                {feature.icon}
              </div>
            </div>
            <h3 className="text-xl font-semibold group-hover:text-purple-400 transition-colors duration-300">
              {feature.title}
            </h3>
            <p className="text-gray-400 mt-3 text-sm leading-relaxed">{feature.desc}</p>

            {/* Animated underline on hover */}
            <div className="mt-4 h-0.5 bg-gradient-to-r from-purple-500 to-transparent w-0 group-hover:w-full transition-all duration-500"></div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Features;